import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Sidebar, MobileNav, CreateThemeModal } from '@client/components';
import { PyrenzBlueButtonWithLoading, PyrenzCard } from '@client/theme';
import { Utils } from '@client/utility';
import { useUserStore } from '@client/store';

interface ThemeType {
  theme_link: string;
  theme_description: string;
  theme_creator: string;
  theme_name?: string;
}

export function ThemesPage() {
  const [themes, setThemes] = useState<ThemeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const userUUID = useUserStore((state) => state.userUUID);

  useEffect(() => {
    const fetchThemes = async () => {
      setLoading(true);
      try {
        const result = await Utils.db.select<ThemeType>({
          tables: 'themes',
          columns: 'theme_link, theme_description, theme_creator, theme_name',
        });
        setThemes(result.data || []);
      } catch {
        setThemes([]);
      } finally {
        setLoading(false);
      }
    };
    if (userUUID) fetchThemes();
  }, [userUUID]);

  const filteredThemes = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return themes.filter(
      (t) =>
        t.theme_link.toLowerCase().includes(term) ||
        t.theme_description.toLowerCase().includes(term) ||
        t.theme_creator.toLowerCase().includes(term) ||
        (t.theme_name?.toLowerCase().includes(term) ?? false)
    );
  }, [searchTerm, themes]);

  const handleThemeCreated = (newTheme: ThemeType) => {
    setThemes((prev) => [...prev, newTheme]);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', width: '100%' }}>
      {/* Sidebar and MobileNav */}
      {!isMobile && <Sidebar />}
      {isMobile && <MobileNav setShowLoginModal={() => {}} />}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          p: 3,
        }}
      >
        {/* Header with title and create button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            maxWidth: 'lg',
            mb: 2,
          }}
        >
          <Typography variant="h4">Themes</Typography>
          <PyrenzBlueButtonWithLoading onClick={() => setCreateModalOpen(true)}>
            Create Theme
          </PyrenzBlueButtonWithLoading>
        </Box>

        {/* Search */}
        <TextField
          label="Search themes"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            sx: {
              backgroundColor: '#000',
              color: '#fff',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: '#555' },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#888',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#fff',
              },
              '& input': { color: '#fff' },
            },
          }}
          InputLabelProps={{
            sx: { color: '#aaa', '&.Mui-focused': { color: '#fff' } },
          }}
        />

        {/* Themes Grid */}
        <Box sx={{ width: '100%', mt: 2 }}>
          {loading ? (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              }}
            >
              {Array.from({ length: 6 }).map((_, i) => (
                <PyrenzCard key={i} loading />
              ))}
            </Box>
          ) : filteredThemes.length === 0 ? (
            <Typography color="text.secondary" mt={2}>
              No themes found.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gap: 2,
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              }}
            >
              {filteredThemes.map((themeItem, index) => (
                <PyrenzCard key={index}>
                  <Typography variant="h6" mb={1}>
                    {themeItem.theme_name || 'Untitled'}
                  </Typography>
                  <Typography variant="body2" mb={1}>
                    {themeItem.theme_description}
                  </Typography>
                  <Typography
                    variant="caption"
                    display="block"
                    mb={1}
                    color="#aaa"
                  >
                    Created by: {themeItem.theme_creator}
                  </Typography>
                  <PyrenzBlueButtonWithLoading
                    dataState={loading ? 'loading' : undefined}
                    onClick={() => {
                      const link = document.createElement('a');
                      link.href = themeItem.theme_link;
                      link.download = themeItem.theme_name
                        ? `${themeItem.theme_name}.zip`
                        : 'theme.zip';
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                    }}
                  >
                    Download Theme
                  </PyrenzBlueButtonWithLoading>
                </PyrenzCard>
              ))}
            </Box>
          )}
        </Box>
      </Box>

      <CreateThemeModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onThemeCreated={handleThemeCreated}
      />
    </Box>
  );
}
